import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { useCurrency } from '../../context/CurrencyContext';
import { 
  FileText, Calendar, DollarSign, ShoppingCart, Clock,
  TrendingUp, CreditCard, Banknote, Printer, Download,
  ChevronLeft, ChevronRight, RefreshCw, PieChart
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { format, subDays, addDays, startOfDay } from 'date-fns';

export default function EODReports() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { selectedRestaurant } = useRestaurant();
  const { formatPrice } = useCurrency();
  
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedRestaurant) {
      fetchReport();
    }
  }, [selectedRestaurant, selectedDate]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/restaurants/${selectedRestaurant.id}/reports/eod?date=${selectedDate}`
      );
      setReport(response.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousDay = () => {
    const prevDay = subDays(new Date(selectedDate), 1);
    setSelectedDate(format(prevDay, 'yyyy-MM-dd'));
  };

  const handleNextDay = () => {
    const nextDay = addDays(new Date(selectedDate), 1);
    const today = startOfDay(new Date());
    if (nextDay <= today) {
      setSelectedDate(format(nextDay, 'yyyy-MM-dd'));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    if (!report) return;
    
    // Create CSV content
    let csv = `End of Day Report - ${selectedDate}\n`;
    csv += `Restaurant: ${selectedRestaurant.name}\n\n`;
    
    csv += `SUMMARY\n`;
    csv += `Total Orders,${report.summary.total_orders}\n`;
    csv += `Total Revenue,${formatPrice(report.summary.total_revenue)}\n`;
    csv += `Total Tips,${formatPrice(report.summary.total_tips)}\n`;
    csv += `Average Order Value,${formatPrice(report.summary.average_order_value)}\n`;
    csv += `Total Voids,${report.summary.total_voids}\n`;
    csv += `Void Amount,${formatPrice(report.summary.void_amount)}\n`;
    csv += `Net Revenue,${formatPrice(report.summary.net_revenue)}\n\n`;
    
    csv += `ORDERS BY TYPE\n`;
    csv += `Type,Count,Revenue\n`;
    Object.entries(report.orders_by_type).forEach(([type, data]) => {
      csv += `${type},${data.count},${formatPrice(data.revenue)}\n`;
    });
    csv += `\n`;
    
    csv += `PAYMENT METHODS\n`;
    csv += `Method,Count,Total\n`;
    Object.entries(report.payment_methods).forEach(([method, data]) => {
      csv += `${method},${data.count},${formatPrice(data.total)}\n`;
    });
    csv += `\n`;
    
    csv += `TOP ITEMS\n`;
    csv += `Item,Quantity,Revenue\n`;
    report.top_items.forEach(item => {
      csv += `${item.name},${item.quantity},${formatPrice(item.revenue)}\n`;
    });
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `eod-report-${selectedDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Report exported');
  };

  if (!selectedRestaurant) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-slate-500">No restaurant selected</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 print:space-y-4" data-testid="eod-reports">
      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">End of Day Report</h1>
          <p className="text-slate-500">Daily sales summary and breakdown</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExport} disabled={!report}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={handlePrint} disabled={!report}>
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Date Selector */}
      <Card className="print:hidden">
        <CardContent className="py-4">
          <div className="flex items-center justify-center gap-4">
            <Button variant="outline" size="icon" onClick={handlePreviousDay}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-400" />
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={format(new Date(), 'yyyy-MM-dd')}
                className="w-44"
                data-testid="date-selector"
              />
            </div>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={handleNextDay}
              disabled={selectedDate >= format(new Date(), 'yyyy-MM-dd')}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={fetchReport} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Print Header */}
      <div className="hidden print:block text-center mb-6">
        <h1 className="text-xl font-bold">{selectedRestaurant.name}</h1>
        <p className="text-sm">End of Day Report - {format(new Date(selectedDate), 'MMMM d, yyyy')}</p>
        <p className="text-xs text-slate-500">Generated: {format(new Date(), 'PPpp')}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : !report ? (
        <div className="text-center py-12 text-slate-500">
          No data available for this date
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:grid-cols-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg print:bg-blue-50">
                    <ShoppingCart className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{report.summary.total_orders}</p>
                    <p className="text-xs text-slate-500">Total Orders</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg print:bg-green-50">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatPrice(report.summary.total_revenue)}</p>
                    <p className="text-xs text-slate-500">Total Revenue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg print:bg-purple-50">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatPrice(report.summary.average_order_value)}</p>
                    <p className="text-xs text-slate-500">Avg Order</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg print:bg-amber-50">
                    <Banknote className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatPrice(report.summary.total_tips)}</p>
                    <p className="text-xs text-slate-500">Tips</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Orders by Type & Payment Methods */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Orders by Type */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Orders by Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(report.orders_by_type).map(([type, data]) => (
                    <div key={type} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="outline"
                          className={
                            type === 'dine_in' ? 'border-cyan-500 text-cyan-600' :
                            type === 'takeaway' ? 'border-orange-500 text-orange-600' :
                            'border-purple-500 text-purple-600'
                          }
                        >
                          {type === 'dine_in' ? 'Dine In' : 
                           type === 'takeaway' ? 'Takeaway' : 'Delivery'}
                        </Badge>
                        <span className="text-slate-500">× {data.count}</span>
                      </div>
                      <span className="font-bold">{formatPrice(data.revenue)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Payment Methods */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Payment Methods</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(report.payment_methods).map(([method, data]) => {
                    const displayMethod = method === 'null' || method === 'unknown' || !method 
                      ? 'Not Recorded' 
                      : method;
                    return (
                      <div key={method} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {method === 'cash' ? (
                            <Banknote className="w-4 h-4 text-green-600" />
                          ) : method === 'card' ? (
                            <CreditCard className="w-4 h-4 text-blue-600" />
                          ) : (
                            <CreditCard className="w-4 h-4 text-slate-400" />
                          )}
                          <span className="capitalize">{displayMethod}</span>
                          <span className="text-slate-500">× {data.count}</span>
                        </div>
                        <span className="font-bold">{formatPrice(data.total)}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Hourly Breakdown */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Hourly Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(report.hourly_breakdown).length === 0 ? (
                <p className="text-slate-500 text-center py-4">No orders during this period</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {Object.entries(report.hourly_breakdown).map(([hour, data]) => (
                    <div 
                      key={hour}
                      className="p-3 bg-slate-50 rounded-lg text-center"
                    >
                      <p className="text-xs text-slate-500">{hour}</p>
                      <p className="font-bold">{data.count} orders</p>
                      <p className="text-sm text-slate-600">{formatPrice(data.revenue)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Selling Items */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <PieChart className="w-5 h-5" />
                Top Selling Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              {report.top_items.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No items sold</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">#</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-center">Qty Sold</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.top_items.slice(0, 10).map((item, index) => (
                      <TableRow key={item.name}>
                        <TableCell className="font-bold text-slate-400">
                          {index + 1}
                        </TableCell>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-center">{item.quantity}</TableCell>
                        <TableCell className="text-right font-bold">
                          {formatPrice(item.revenue)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Voids Section */}
          {(report.summary.total_voids > 0 || report.voids?.length > 0) && (
            <Card className="border-red-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-red-600">Voids & Refunds</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6 mb-4">
                  <div>
                    <p className="text-sm text-slate-500">Total Voids</p>
                    <p className="text-xl font-bold">{report.summary.total_voids}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Void Amount</p>
                    <p className="text-xl font-bold text-red-600">
                      -{formatPrice(report.summary.void_amount)}
                    </p>
                  </div>
                </div>
                {report.voids?.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.voids.map((v, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm">
                            {v.created_at ? format(new Date(v.created_at), 'HH:mm') : '-'}
                          </TableCell>
                          <TableCell>{v.item_name || 'Unknown'}</TableCell>
                          <TableCell>{v.reason || '-'}</TableCell>
                          <TableCell className="text-right text-red-600">
                            -{formatPrice(v.amount || 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}

          {/* Net Summary */}
          <Card className="bg-slate-900 text-white print:bg-slate-100 print:text-slate-900">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 print:text-slate-600">Net Revenue</p>
                  <p className="text-3xl font-bold">
                    {formatPrice(report.summary.net_revenue)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-slate-400 print:text-slate-600 text-sm">
                    Gross: {formatPrice(report.summary.total_revenue)}
                  </p>
                  {report.summary.void_amount > 0 && (
                    <p className="text-red-400 print:text-red-600 text-sm">
                      Voids: -{formatPrice(report.summary.void_amount)}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
